import { OrderPdfStatus } from 'src/entities/order/model/order-status.model';

export interface CreatePurchaseNoteItemInput {
  serviceId: string;
  ticketIds?: string[] | null;
}

export interface CreatePurchaseNoteCreatedByInput {
  name: string;
  email: string;
}

export interface CreatePurchaseNoteCommissionInput {
  type: 'PERCENTAGE' | 'FIXED';
  value: number;
}

export interface CreatePurchaseNoteInput {
  items: CreatePurchaseNoteItemInput[];
  createdBy: CreatePurchaseNoteCreatedByInput;
  /** Fecha de revisión (ISO o YYYY-MM-DD). No usar scheduledPaymentDate. */
  reviewDate?: string | null;
  reservationNumber?: string | null;
  purchaseNoteObservation?: string | null;
  commission?: CreatePurchaseNoteCommissionInput | null;
}

export interface CreatedPurchaseNote {
  purchaseNoteId: string;
  purchaseNoteNumber: string;
  serviceIds: string[];
  ticketIds?: string[] | null;
  idItinerary?: string | null;
  providerId?: string | null;
  currency?: string | null;
  purchaseNoteStatus?: string | null;
  reviewDate?: string | null;
  reservationNumber?: string | null;
  purchaseNoteObservation?: string | null;
  pdfStatus?: OrderPdfStatus | string | null;
  baseAmount?: number | null;
  commissionPercent?: number | null;
  commissionAmount?: number | null;
  total?: number | null;
}

export type RejectedPurchaseNoteServiceReason =
  | 'DUPLICATED_INPUT'
  | 'SERVICE_NOT_FOUND'
  | 'SERVICE_DELETED'
  | 'SERVICE_ALREADY_HAS_PURCHASE_NOTE'
  | 'MISSING_AMOUNT'
  | string;

export interface RejectedPurchaseNoteService {
  serviceId: string;
  ticketId?: string | null;
  reason: RejectedPurchaseNoteServiceReason;
  message: string;
}

export interface PurchaseNoteWarning {
  serviceId?: string | null;
  groupKey?: string | null;
  reason: string;
  message: string;
}

export interface CreatePurchaseNoteResult {
  createdPurchaseNotes: CreatedPurchaseNote[];
  rejectedServices: RejectedPurchaseNoteService[];
  warnings: PurchaseNoteWarning[];
}

export enum CreatePurchaseNoteJobStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export interface CreatePurchaseNoteJobResult {
  jobId: string;
  status: CreatePurchaseNoteJobStatus | string;
  errorCode?: string | null;
  lastError?: string | null;
  createdPurchaseNotes?: CreatedPurchaseNote[] | null;
  rejectedServices?: RejectedPurchaseNoteService[] | null;
  warnings?: PurchaseNoteWarning[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Prompt: poll ~1s. */
export const CREATE_PURCHASE_NOTE_JOB_POLL_INTERVAL_MS = 1000;
export const CREATE_PURCHASE_NOTE_JOB_TIMEOUT_MS = 180_000;

export function isCreatePurchaseNoteJobInProgress(
  status: string | null | undefined
): boolean {
  const normalized = status?.trim().toUpperCase();
  return (
    normalized === CreatePurchaseNoteJobStatus.PENDING ||
    normalized === CreatePurchaseNoteJobStatus.PROCESSING
  );
}

export function toCreatePurchaseNoteResult(
  job: CreatePurchaseNoteJobResult | null | undefined
): CreatePurchaseNoteResult {
  return {
    createdPurchaseNotes: job?.createdPurchaseNotes ?? [],
    rejectedServices: job?.rejectedServices ?? [],
    warnings: job?.warnings ?? []
  };
}
